// Articles, issues and editors. Data lives in public/data/*.json (see codeDataStore.js);
// there is no database. The file keeps its old name so existing imports keep working.
import { loadData, saveToCode } from './codeDataStore';

export function sortArticlesBySequence(articles) {
  if (!Array.isArray(articles)) return [];
  return [...articles].sort((a, b) => {
    // 1. Numerical articleNumber (e.g. 1, 2, 3...)
    const numA = parseInt(a.articleNumber ?? a.sequence ?? a.order ?? a.articleIndex, 10);
    const numB = parseInt(b.articleNumber ?? b.sequence ?? b.order ?? b.articleIndex, 10);
    const hasNumA = !isNaN(numA);
    const hasNumB = !isNaN(numB);

    if (hasNumA && hasNumB && numA !== numB) {
      return numA - numB;
    }
    if (hasNumA && !hasNumB) return -1;
    if (!hasNumA && hasNumB) return 1;

    // 2. Editorial note is always first
    if (a.type === 'editorialNote' && b.type !== 'editorialNote') return -1;
    if (b.type === 'editorialNote' && a.type !== 'editorialNote') return 1;

    // 3. Fallback to createdAt or date ascending
    const timeA = new Date(a.createdAt || a.date || 0).getTime();
    const timeB = new Date(b.createdAt || b.date || 0).getTime();
    return timeA - timeB;
  });
}

// ---------- Articles ----------

export async function getJournals() {
  const list = await loadData('journals', []);
  return sortArticlesBySequence(Array.isArray(list) ? list : []);
}

export async function saveLocalJournals(updatedList) {
  await saveToCode('journals', sortArticlesBySequence(updatedList));
}

export async function addJournal(journal) {
  const current = await getJournals();
  const newEntry = {
    id: 'journal_' + Date.now(),
    ...journal,
    createdAt: new Date().toISOString()
  };
  await saveLocalJournals([newEntry, ...current]);
  return newEntry;
}

export async function getArticleById(id) {
  const journals = await getJournals();
  return journals.find(j =>
    String(j.id) === String(id) ||
    String(j.friendlyUrl) === String(id) ||
    String(j.articleNumber) === String(id)
  ) || null;
}

// ---------- Editors ----------

export async function getExecutiveEditors() {
  const list = await loadData('editors', []);
  return Array.isArray(list) ? list : [];
}

export async function saveLocalEditors(updatedList) {
  await saveToCode('editors', updatedList);
}

// ---------- Issues ----------

const sanitizeDateValue = (val) => {
  if (!val) return '';
  if (typeof val === 'object') {
    const sec = val.seconds || val._seconds;
    if (sec) return new Date(sec * 1000).toISOString().slice(0, 10);
    if (typeof val.toDate === 'function') return val.toDate().toISOString().slice(0, 10);
    return '';
  }
  return String(val);
};

const sanitizeVerboseIssue = (issue) => {
  if (!issue) return issue;
  return {
    ...issue,
    createdAt: sanitizeDateValue(issue.createdAt),
    validity: typeof issue.validity === 'object' ? sanitizeDateValue(issue.validity) : (issue.validity || ''),
  };
};

export async function getVerboseIssues() {
  const list = await loadData('issues', []);
  return (Array.isArray(list) ? list : []).map(sanitizeVerboseIssue);
}

export async function getVerboseIssueById(id) {
  const issues = await getVerboseIssues();
  return issues.find(i => String(i.id) === String(id) || String(i.Vol) === String(id)) || issues[0] || null;
}

export async function addVerboseIssue(data) {
  const current = await getVerboseIssues();
  const newEntry = { id: 'issue_' + Date.now(), ...data };
  await saveToCode('issues', [newEntry, ...current]);
  return newEntry;
}

export async function updateVerboseIssue(id, data) {
  const current = await getVerboseIssues();
  await saveToCode('issues', current.map(item => item.id === id ? { ...item, ...data } : item));
  return true;
}

export async function deleteVerboseIssue(id) {
  const current = await getVerboseIssues();
  await saveToCode('issues', current.filter(item => item.id !== id));
  return true;
}
